import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { CapacitorHttp } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

const DL_NOTIF_ID = 10001;      // any stable integer
const DL_CHANNEL_ID = 'downloads';

// call once somewhere early in app startup (e.g. App.tsx/App.js useEffect)
export async function setupDownloadNotifications() {
    try {
        // Ask permission (Android will auto-grant if declared in manifest)
        await LocalNotifications.requestPermissions();
        // Create a high-importance channel for Android 8+
        await LocalNotifications.createChannel({
            id: DL_CHANNEL_ID,
            name: 'Downloads',
            importance: 5, // IMPORTANCE_HIGH
            visibility: 1, // PUBLIC
            sound: undefined,
            vibration: true,
            lights: true,
        });
    } catch (e) { /* no-op */ }
}

// small helpers
async function showDownloadingNotification(filename) {
    try {
        await LocalNotifications.schedule({
            notifications: [{
                id: DL_NOTIF_ID,
                title: 'Downloading…',
                body: filename || 'Please wait',
                smallIcon: 'ic_stat_download', // drawable resource name (no extension)
                channelId: DL_CHANNEL_ID,
                ongoing: true,                 // makes it sticky
                autoCancel: false,
            }],
        });
    } catch (e) { /* ignore */ }
}

async function showDoneNotification(filename, whereText) {
    try {
        // Replace the ongoing one with a success one
        await LocalNotifications.cancel({ notifications: [{ id: DL_NOTIF_ID }] });
        await LocalNotifications.schedule({
            notifications: [{
                id: DL_NOTIF_ID + 1,
                title: 'Download complete',
                body: filename ? `${filename}${whereText ? ` • ${whereText}` : ''}` : 'Saved',
                smallIcon: 'ic_stat_download',
                channelId: DL_CHANNEL_ID,
                ongoing: false,
                autoCancel: true,
            }],
        });
    } catch (e) { /* ignore */ }
}

async function showFailNotification(message) {
    try {
        await LocalNotifications.cancel({ notifications: [{ id: DL_NOTIF_ID }] });
        await LocalNotifications.schedule({
            notifications: [{
                id: DL_NOTIF_ID + 2,
                title: 'Download failed',
                body: message || 'Please try again',
                channelId: DL_CHANNEL_ID,
                autoCancel: true,
            }],
        });
    } catch (e) { /* ignore */ }
}

// ---- YOUR function with notifications added ----
export async function callFunctionToDownload(url) {
    try {
        const isNative = Capacitor.isNativePlatform && Capacitor.isNativePlatform();

        if (!isNative) {
            // ---- Web (no tray notifications here) ----
            const res = await fetch(url, { credentials: 'omit' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const headers = normalizeHeaders(Object.fromEntries(res.headers.entries()));
            const filename = pickFilename(url, headers);
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
            return { ok: true, mode: 'web', filename, path: null };
        }

        // ---- Native (Android/iOS) ----
        // Prepare notifications
        const startHeaders = {};
        const startFilename = pickFilename(url, startHeaders); // tentative name if headers missing
        await showDownloadingNotification(startFilename);

        const httpRes = await CapacitorHttp.request({
            method: 'GET',
            url,
            responseType: 'arraybuffer',
        });

        const headers = normalizeHeaders(httpRes.headers || {});
        const filename = pickFilename(url, headers);

        let base64Data;
        if (httpRes?.data?.byteLength !== undefined) {
            base64Data = arrayBufferToBase64(httpRes.data);
        } else if (typeof httpRes?.data === 'string') {
            base64Data = httpRes.data; // already base64
        } else {
            throw new Error('Unexpected HTTP data format');
        }

        const platform = Capacitor.getPlatform && Capacitor.getPlatform();

        // ---- Choose target directory/path ----
        let directory, path;
        if (platform === 'android') {
            try { await Filesystem.requestPermissions(); } catch (_) {console.log('')}
            directory = Directory.ExternalStorage;
            path = `Download/${filename}`; // public Downloads
        } else {
            directory = Directory.Documents;
            path = filename;
        }

        // Write file (fallback for Android if needed)
        try {
            await Filesystem.writeFile({
                path, data: base64Data, directory, recursive: true, encoding: 'base64',
            });
        } catch (err) {
            if (platform === 'android') {
                directory = Directory.Cache;
                path = `downloads/${filename}`;
                await Filesystem.writeFile({
                    path, data: base64Data, directory, recursive: true, encoding: 'base64',
                });
            } else {
                throw err;
            }
        }

        const { uri } = await Filesystem.getUri({ directory, path });

        // Success notification
        const whereText =
            platform === 'android'
                ? (directory === Directory.ExternalStorage && path.startsWith('Download/'))
                    ? 'Downloads'
                    : 'App cache'
                : 'Documents';
        await showDoneNotification(filename, whereText);

        return {
            ok: true,
            mode: 'native',
            filename,
            uri,
            directory,
            path,
            savedToDownloads: platform === 'android' && directory === Directory.ExternalStorage && path.startsWith('Download/'),
        };

    } catch (err) {
        await showFailNotification(err?.message);
        return { ok: false, error: err?.message || String(err) };
    }
}

/* ---------------- helpers (unchanged) ---------------- */

function normalizeHeaders(h) {
    const out = {};
    Object.keys(h || {}).forEach(k => (out[k.toLowerCase()] = h[k]));
    return out;
}

function pickFilename(url, headers) {
    const cd = headers && headers['content-disposition'];
    if (cd) {
        const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
        const raw = decodeURIComponent((m && (m[1] || m[2] || '')).trim());
        if (raw) return sanitizeFilename(raw);
    }
    try {
        const u = new URL(url);
        const last = (u.pathname.split('/').pop() || '').trim();
        if (last) return sanitizeFilename(last);
    } catch (_) {console.log('')}
    return `download-${Date.now()}.pdf`;
}

function sanitizeFilename(name) {
    return name.replace(/[\\/:*?"<>|]/g, '_').slice(0, 200) || `download-${Date.now()}.pdf`;
}

function arrayBufferToBase64(ab) {
    const bytes = new Uint8Array(ab);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

